import React, { useMemo } from 'react';

import { FormError, FormFieldProvider, useForm } from '@tanium/react-form-context';
import { FormRow as FormRowUi, FormRowProps as FormRowUiProps } from '@tanium/react-form-ui';

export interface FormRowProps<K>
  extends Pick<FormRowUiProps, 'label' | 'required' | 'description' | 'helpText' | 'labelId'> {
  /**
   * Key to associate the row with the value / error objects from the enclosing Form (e.g. to
   * associate error messages to a particular field for validation message display). By default,
   * FormInputs within the FormRow are associated with the dataKey of the containing FormRow.
   */
  dataKey?: K;
  children: React.ReactNode;
  errors?: FormError | FormError[];
}
const FormRow = ({
  children,
  dataKey,
  label,
  labelId,
  description,
  helpText,
  required,
  errors: errorsProp,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
FormRowProps<any>) => {
  const errorsPropArray = useMemo(
    () => (errorsProp ? ([] as FormError[]).concat(errorsProp) : undefined),
    [errorsProp],
  );
  const { prefix, errors } = useForm();
  const inputErrors = errorsPropArray || (dataKey && errors[dataKey as keyof typeof errors]);
  const prefixedLabel = labelId || (dataKey && `${prefix}-${dataKey}`);

  return (
    <FormFieldProvider dataKey={dataKey} label={label} labelId={labelId} errors={errorsPropArray}>
      <FormRowUi
        description={description}
        errors={inputErrors}
        helpText={helpText}
        label={label}
        labelId={prefixedLabel}
        required={required}
      >
        {children}
      </FormRowUi>
    </FormFieldProvider>
  );
};

export default FormRow;
